import { useQuery } from '@tanstack/react-query';
import { fetchCommitAssignees, type ICommitAssignee } from '@apis/activities';
import { commitAssigneesKey } from '@constants/queryKeys';
import type { SiteId } from '@interfaces/IScheduler';

export interface ICommitAssignees {
  /** The project's users — what the assignee chips offer. Empty until loaded. */
  candidates: readonly ICommitAssignee[];
  /** The caller's username: the default assignee. Null while unknown. */
  me: string | null;
  /** The list has answered, or failed — either way the default is decided. */
  settled: boolean;
}

const NO_CANDIDATES: readonly ICommitAssignee[] = [];

/** Who a promise can name as accountable, and who "me" is among them — the one
 *  query both commit reviews (single and list form) ask. */
export function useCommitAssignees(siteId: SiteId): ICommitAssignees {
  const query = useQuery({
    queryKey: commitAssigneesKey(siteId),
    queryFn: ({ signal }) => fetchCommitAssignees(siteId, signal),
    enabled: !!siteId,
    staleTime: 5 * 60_000,
  });
  return {
    candidates: query.data ?? NO_CANDIDATES,
    me: query.data?.find((candidate) => candidate.isYou)?.username ?? null,
    settled: query.isSuccess || query.isError,
  };
}
